import React from "react";
import { Link } from "react-router";
import { useAuth } from "../../context/AuthContext";
import { useBlobUrl } from "../../hooks/useBlobUrl";
import { apiUrl } from "../../utils/api";
import { I } from "../redesign/kvpIcons";
import { AnhangEntwurf, ticketService } from "./ticketService";
import { LoesungsVorschlag } from "./LoesungsVorschlag";
import { TelefonieBlock } from "./TelefonieBlock";
import {
  ART_LABEL, BEREICH_LABEL, PRIO_LABEL, STATUS_LABEL, bereichAuswahlLabel, bereichOptionen,
  TicketAnhangInfo, TicketBereich, TicketDetail as Detail, TicketEintrag, TicketPrio, TicketStatus,
} from "./ticketTypen";
import { pruefeAnhaenge } from "./anhangRegeln";
import { istRasterBild } from "./anhangArt";
import { fehlerText } from "./ticketFehler";
import { istInterneRoute } from "./routenRegeln";
import { BereichAufnahme } from "./screenshot/BereichAufnahme";
import { MarkierEditor } from "./screenshot/MarkierEditor";
import "./tickets.css";

const datum = (iso: string) =>
  new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

const BEZUG_LINK: Record<string, (id: string) => string> = {
  vorgang: (id) => `/vorgaenge/${id}`, todo: (id) => `/todos/${id}`, kontakt: (id) => `/contacts/${id}`,
  risiko: (id) => `/risks/${id}`, vertrag: (id) => `/contracts/${id}`, schaden: (id) => `/schaeden/${id}`,
};

/**
 * Nur Raster-Bilder werden beim Öffnen des Tickets automatisch geladen
 * (`useBlobUrl` holt sofort die Bytes). Alles andere ist `DateiVorschau`, die
 * erst beim Klick lädt — sonst würde „Ticket öffnen" jeden angehängten
 * PDF/Office-Anhang blind herunterladen (siehe SHOULD-FIX S2). Die Aufteilung
 * in zwei Komponenten hält `useBlobUrl` unbedingt (nicht bedingt) aufgerufen.
 */
function BildVorschau({ ticketId, a }: { ticketId: number; a: TicketAnhangInfo }) {
  const { url } = useBlobUrl(ticketService.anhangPfad(ticketId, a.id));
  if (!url) return <span className="tk-datei">{a.dateiname}</span>;
  return (
    <a href={url} target="_blank" rel="noreferrer">
      <img src={url} alt={a.dateiname} />
    </a>
  );
}

function DateiVorschau({ ticketId, a }: { ticketId: number; a: TicketAnhangInfo }) {
  const [laedt, setLaedt] = React.useState(false);

  const herunterladen = async () => {
    setLaedt(true);
    try {
      const res = await fetch(apiUrl(ticketService.anhangPfad(ticketId, a.id)));
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = a.dateiname;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      // Anhang bleibt als Knopf sichtbar — Nutzer kann es erneut versuchen.
    } finally {
      setLaedt(false);
    }
  };

  return (
    <button type="button" className="tk-datei tk-datei-btn" onClick={() => void herunterladen()} disabled={laedt}>
      {laedt ? "Lädt …" : a.dateiname}
    </button>
  );
}

function Vorschau({ ticketId, a }: { ticketId: number; a: TicketAnhangInfo }) {
  return istRasterBild(a.mimeType)
    ? <BildVorschau ticketId={ticketId} a={a} />
    : <DateiVorschau ticketId={ticketId} a={a} />;
}

function eintragText(e: TicketEintrag): string {
  if (e.art === "status") return `hat den Status auf „${STATUS_LABEL[e.neu as TicketStatus] ?? e.neu}“ gesetzt`;
  if (e.art === "prio") return e.neu ? `hat die Priorität auf „${PRIO_LABEL[e.neu as TicketPrio]}“ gesetzt` : "hat die Priorität entfernt";
  if (e.art === "bereich") return `hat den Bereich auf „${BEREICH_LABEL[e.neu as TicketBereich] ?? e.neu}“ geändert`;
  if (e.art === "archiv") return e.neu === "archiviert" ? "hat das Ticket archiviert" : "hat das Ticket aus dem Archiv geholt";
  return e.art === "ergaenzung" ? "hat ergänzt" : "hat kommentiert";
}

type Props = { id: number; onGeaendert: () => void; onZurueck: () => void };

/** „10:31“ aus einem ISO-Zeitpunkt — leer, wenn unlesbar. */
const uhrzeit = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
};

export function TicketDetail({ id, onGeaendert, onZurueck }: Props) {
  const { isAdmin, isGfgl, user } = useAuth();
  const entscheidet = isAdmin || isGfgl;
  const [t, setT] = React.useState<Detail | null>(null);
  const [fehler, setFehler] = React.useState<string | null>(null);
  const [art, setArt] = React.useState<"kommentar" | "ergaenzung">("kommentar");
  const [text, setText] = React.useState("");
  const [anhaenge, setAnhaenge] = React.useState<AnhangEntwurf[]>([]);
  const [phase, setPhase] = React.useState<"aus" | "aufnahme" | "markieren">("aus");
  const [roh, setRoh] = React.useState<HTMLCanvasElement | null>(null);
  const [ablehnen, setAblehnen] = React.useState<string | null>(null);
  /** Text zur Rücksprache (optional) — geht mit der Glocke an den Melder. `null` = Feld zu. */
  const [ruecksprache, setRuecksprache] = React.useState<string | null>(null);
  const dateiRef = React.useRef<HTMLInputElement>(null);

  const laden = React.useCallback(async () => {
    try { setT(await ticketService.detail(id)); setFehler(null); }
    catch (e) { setFehler(fehlerText(e, "Ticket konnte nicht geladen werden.")); }
  }, [id]);
  React.useEffect(() => { void laden(); }, [laden]);

  const aktion = async (f: () => Promise<unknown>) => {
    try { await f(); await laden(); onGeaendert(); }
    catch (e) { setFehler(fehlerText(e, "Aktion fehlgeschlagen. Bitte später erneut versuchen.")); }
  };

  if (fehler && !t) return <div className="tp-err">{fehler}</div>;
  if (!t) return <div className="tp-empty">Lädt …</div>;

  const darfBereich = entscheidet || t.erstelltVon === user?.id;
  const knopf = (label: string, status: TicketStatus) => (
    <button type="button" className="tp-detail-btn" onClick={() => aktion(() => ticketService.status(t.id, status))}>{label}</button>
  );

  if (phase === "aufnahme") {
    return <BereichAufnahme onFertig={(c) => { setRoh(c); setPhase("markieren"); }} onAbbruch={(m) => { setFehler(m ?? null); setPhase("aus"); }} />;
  }
  if (phase === "markieren" && roh) {
    return (
      <MarkierEditor bild={roh}
        onFertig={(png) => { setAnhaenge((a) => [...a, { datei: png, name: `screenshot-${a.length + 1}.png`, art: "screenshot" }]); setRoh(null); setPhase("aus"); }}
        onAbbruch={() => { setRoh(null); setPhase("aus"); }} />
    );
  }

  const hauptAnhaenge = t.anhaenge.filter((a) => a.eintragId == null);

  return (
    <div className="tk-detail">
      <div className="tk-detail-inhalt">
        <button type="button" className="tp-back" onClick={onZurueck}>{I.back} Zurück zur Übersicht</button>

        <header className="tk-detail-kopf">
          <div className="tk-kopf-zeile">
            <span className="tk-nr">T-{t.id}</span>
            <h1>{t.betreff}</h1>
          </div>
          <div className="tk-meta">
            <span className={`tk-status tk-status--${t.status}`}>{STATUS_LABEL[t.status]}</span>
            <span className="tk-meta-punkt"><span className={`tk-punkt tk-prio--${t.prio ?? "keine"}`} />{t.prio ? PRIO_LABEL[t.prio] : "Prio ungesetzt"}</span>
            <span>{ART_LABEL[t.art]}</span>
            <span>{BEREICH_LABEL[t.bereich] ?? t.bereich}</span>
            {t.tenantId !== "default" && <span className="tk-leise">Mandant {t.tenantId}</span>}
            {t.archiviertAm && <span className="tk-archiviert">Archiviert</span>}
          </div>

          {(entscheidet || darfBereich) && (
            <div className="tk-aktionen">
              {entscheidet && (t.status === "offen" || t.status === "ruecksprache") && knopf("Freigeben", "freigegeben")}
              {entscheidet && (t.status === "offen" || t.status === "freigegeben") && (
                <button type="button" className="tp-detail-btn" onClick={() => { setAblehnen(null); setRuecksprache(""); }}>
                  Rücksprache
                </button>
              )}
              {entscheidet && (t.status === "offen" || t.status === "ruecksprache") && (
                <button type="button" className="tp-detail-btn" onClick={() => { setRuecksprache(null); setAblehnen(""); }}>Ablehnen</button>
              )}
              {entscheidet && t.status === "freigegeben" && knopf("Erledigt", "erledigt")}
              {entscheidet && t.status !== "offen" && knopf("Wieder öffnen", "offen")}
              {/* Archiv (User 26.10.2026): Kennzeichen neben dem Status, aus jedem Status. */}
              {entscheidet && (
                <button type="button" className="tp-detail-btn"
                  onClick={() => aktion(() => ticketService.archiv(t.id, !t.archiviertAm))}>
                  {t.archiviertAm ? "Aus dem Archiv holen" : "Archivieren"}
                </button>
              )}
              <span className="tk-flex" />
              {isAdmin && (
                <label className="tk-aktion-feld">
                  <span>Prio</span>
                  <select className="tk-select" aria-label="Priorität" value={t.prio ?? ""}
                    onChange={(e) => aktion(() => ticketService.prio(t.id, (e.target.value || null) as TicketPrio | null))}>
                    <option value="">Ungesetzt</option>
                    {(Object.keys(PRIO_LABEL) as TicketPrio[]).map((p) => <option key={p} value={p}>{PRIO_LABEL[p]}</option>)}
                  </select>
                </label>
              )}
              {darfBereich && (
                <label className="tk-aktion-feld">
                  <span>Bereich</span>
                  <select className="tk-select" aria-label="Bereich" value={t.bereich}
                    onChange={(e) => aktion(() => ticketService.bereich(t.id, e.target.value as TicketBereich))}>
                    {bereichOptionen(t.bereich).map((b) => <option key={b} value={b}>{bereichAuswahlLabel(b)}</option>)}
                  </select>
                </label>
              )}
            </div>
          )}
          {ruecksprache !== null && (
            <div className="tk-ablehnen">
              <textarea rows={2} placeholder="Was ist zu klären? (optional — der Melder bekommt den Text mit der Benachrichtigung)"
                value={ruecksprache} onChange={(e) => setRuecksprache(e.target.value)} autoFocus />
              <button type="button" className="tp-detail-btn" onClick={() => setRuecksprache(null)}>Abbrechen</button>
              <button type="button" className="cd-save"
                onClick={() => aktion(async () => {
                  await ticketService.status(t.id, "ruecksprache", ruecksprache.trim() || undefined);
                  setRuecksprache(null);
                })}>
                Rücksprache
              </button>
            </div>
          )}
          {ablehnen !== null && (
            <div className="tk-ablehnen">
              <textarea rows={2} placeholder="Begründung (Pflicht)" value={ablehnen} onChange={(e) => setAblehnen(e.target.value)} autoFocus />
              <button type="button" className="tp-detail-btn" onClick={() => setAblehnen(null)}>Abbrechen</button>
              <button type="button" className="cd-save" disabled={!ablehnen.trim()}
                onClick={() => aktion(async () => { await ticketService.status(t.id, "abgelehnt", ablehnen.trim()); setAblehnen(null); })}>
                Ablehnen
              </button>
            </div>
          )}
        </header>

        {fehler && <p className="tk-fehler" role="alert">{fehler}</p>}

        {/* Route/Bezug/Ansicht sind Arbeitswissen für die, die entscheiden (admin/gfgl). */}
        {entscheidet && (
          <section className="tk-abschnitt">
            <h3>Wo</h3>
            {(t.bezugId || t.ansicht) && (
              <p className="tk-wo-zeile">
                {t.bezugTyp && t.bezugId && BEZUG_LINK[t.bezugTyp] ? (
                  <Link to={BEZUG_LINK[t.bezugTyp](t.bezugId)}>{t.bezugLabel ?? `${t.bezugTyp} ${t.bezugId}`}</Link>
                ) : t.bezugLabel ? <span>{t.bezugLabel}</span> : null}
                {t.bezugId && t.ansicht && " · "}
                {t.ansicht && <span>{t.ansicht}</span>}
              </p>
            )}
            <p className="tk-wo-zeile">
              <span className="tk-leise">Route </span>
              {t.route ? (
                istInterneRoute(t.route) ? <Link to={t.route}>{t.route}</Link> : <span className="tk-leise">{t.route}</span>
              ) : (
                <span className="tk-leise">Kein Seitenbezug</span>
              )}
            </p>
            {!!t.technik?.verlauf?.length && (
              <>
                <h4 className="tk-verlauf-kopf">Vorher besucht</h4>
                <ol className="tk-seitenverlauf">
                  {t.technik.verlauf.map((s, i) => (
                    <li key={`${s.zeit}-${i}`}>
                      <span className="tk-leise">{uhrzeit(s.zeit)}</span>{" "}
                      {istInterneRoute(s.route) ? <Link to={s.route}>{s.route}</Link> : <span>{s.route}</span>}
                      {s.ansicht && <span className="tk-leise"> · {s.ansicht}</span>}
                    </li>
                  ))}
                </ol>
              </>
            )}
          </section>
        )}

        <section className="tk-abschnitt">
          <h3>Beschreibung</h3>
          <p className="tk-text">{t.inhalt || <span className="tk-leise">Keine Beschreibung</span>}</p>
          <p className="tk-leise">Gemeldet von {t.erstelltVonName} · {datum(t.createdAt)}</p>
          {hauptAnhaenge.length > 0 && (
            <ul className="tk-vorschau">{hauptAnhaenge.map((a) => <li key={a.id}><Vorschau ticketId={t.id} a={a} /></li>)}</ul>
          )}
        </section>

        {entscheidet && (
          <section className="tk-abschnitt tk-vorschlag">
            <h3>Lösungsvorschlag</h3>
            <LoesungsVorschlag ticketId={t.id} />
          </section>
        )}

        <section className="tk-abschnitt">
          <h3>Verlauf</h3>
          {t.eintraege.length === 0 && <p className="tk-leise">Noch keine Einträge.</p>}
          <ol className="tk-verlauf">
            {t.eintraege.map((e) => {
              const bilder = t.anhaenge.filter((a) => a.eintragId === e.id);
              return (
                <li key={e.id} className={`tk-eintrag tk-eintrag--${e.art}`}>
                  <div className="tk-eintrag-kopf"><strong>{e.autorName}</strong> {eintragText(e)} · <span className="tk-leise">{datum(e.zeitpunkt)}</span></div>
                  {e.text && <p className="tk-text">{e.text}</p>}
                  {bilder.length > 0 && <ul className="tk-vorschau">{bilder.map((a) => <li key={a.id}><Vorschau ticketId={t.id} a={a} /></li>)}</ul>}
                </li>
              );
            })}
          </ol>
        </section>

        <section className="tk-abschnitt tk-eingabe">
          <div className="rd-seg" role="group" aria-label="Eintragsart">
            <button type="button" className={art === "kommentar" ? "active" : ""} onClick={() => setArt("kommentar")}>Kommentar</button>
            <button type="button" className={art === "ergaenzung" ? "active" : ""} onClick={() => setArt("ergaenzung")}>Ergänzung</button>
          </div>
          <textarea rows={3} value={text} onChange={(e) => setText(e.target.value)}
            placeholder={art === "ergaenzung" ? "Was ist neu am Problem bzw. an der Idee?" : "Rückfrage oder Anmerkung"} />
          <div className="tk-anhang-knoepfe">
            <button type="button" className="tp-detail-btn" onClick={() => setPhase("aufnahme")}>{I.image} Bereich fotografieren</button>
            <button type="button" className="tp-detail-btn" onClick={() => dateiRef.current?.click()}>{I.plus} Datei</button>
            <input ref={dateiRef} type="file" multiple hidden
              onChange={(e) => {
                const f = Array.from(e.target.files ?? []);
                const { uebernehmen, meldung } = pruefeAnhaenge(anhaenge.length, f.map((d) => ({ name: d.name, size: d.size })));
                setFehler(meldung);
                if (uebernehmen.length > 0) {
                  setAnhaenge((a) => [...a, ...uebernehmen.map((i) => ({ datei: f[i], name: f[i].name, art: "datei" as const }))]);
                }
                e.target.value = "";
              }} />
            <span className="tk-flex" />
            <button type="button" className="cd-save" disabled={!text.trim() && !anhaenge.length}
              onClick={() => aktion(async () => { await ticketService.eintrag(t.id, art, text.trim(), anhaenge); setText(""); setAnhaenge([]); })}>
              {art === "ergaenzung" ? "Ergänzen" : "Kommentieren"}
            </button>
          </div>
          {anhaenge.length > 0 && (
            <ul className="tk-anhang-liste">
              {anhaenge.map((a, i) => (
                <li key={`${a.name}-${i}`}>
                  <span className="tk-datei">{a.name}</span>
                  <button type="button" aria-label={`${a.name} entfernen`}
                    onClick={() => setAnhaenge((alt) => alt.filter((_, j) => j !== i))}>✕</button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {entscheidet && t.technik?.telefonie && <TelefonieBlock telefonie={t.technik.telefonie} />}

        {entscheidet && t.technik && (
          <details className="tk-abschnitt tk-technik">
            <summary>Technische Spuren</summary>
            <pre>{JSON.stringify(t.technik, null, 2)}</pre>
          </details>
        )}
      </div>
    </div>
  );
}
